import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
	Button,
	Col,
	Form,
	Input,
	Popconfirm,
	Row,
	Space,
	Table,
	Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import PageTitle from "../../page-header/PageHeader";
import {
	deleteProjectType,
	loadProjectTypes,
	saveProjectType,
} from "../../../redux/rtk/features/projectManagement/project/projectType/projectType";

const ProjectTypeSetup = () => {
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const { list = [], loading } = useSelector((state) => state.projectType || {});

	const [editingRecord, setEditingRecord] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [deletingId, setDeletingId] = useState(null);

	useEffect(() => {
		dispatch(loadProjectTypes());
	}, [dispatch]);

	const resetForm = useCallback(() => {
		form.resetFields();
		setEditingRecord(null);
	}, [form]);

	const handleAddNew = useCallback(() => {
		resetForm();
	}, [resetForm]);

	const handleEdit = useCallback(
		(record) => {
			if (!record) {
				return;
			}
			form.setFieldsValue({
				ProjectTypeName: record?.ProjectTypeName ?? "",
				ProjectTypeDescription: record?.ProjectTypeDescription ?? "",
			});
			setEditingRecord({
				ProjectTypeId: Number(record?.ProjectTypeId) || 0,
				DataStatus: Number(record?.DataStatus) || 1,
			});
		},
		[form]
	);

	const handleCancelEdit = useCallback(() => {
		resetForm();
	}, [resetForm]);

	const handleSubmit = useCallback(async () => {
		try {
			const values = await form.validateFields();
			setSubmitting(true);
			const payload = {
				ProjectTypeId: editingRecord?.ProjectTypeId ?? 0,
				ProjectTypeName: values.ProjectTypeName?.trim(),
				ProjectTypeDescription: values.ProjectTypeDescription?.trim() || null,
				DataStatus: editingRecord?.DataStatus ?? 1,
			};
			const response = await dispatch(saveProjectType(payload));
			if (response?.meta?.requestStatus !== "rejected") {
				resetForm();
				dispatch(loadProjectTypes());
			}
		} finally {
			setSubmitting(false);
		}
	}, [dispatch, editingRecord, form, resetForm]);

	const handleDelete = useCallback(
		async (record) => {
			const projectTypeId = Number(record?.ProjectTypeId);
			if (!projectTypeId) {
				return;
			}
			setDeletingId(projectTypeId);
			try {
				const response = await dispatch(
					deleteProjectType({
						ProjectTypeId: projectTypeId,
					})
				);
				if (response?.meta?.requestStatus !== "rejected") {
					if (editingRecord?.ProjectTypeId === projectTypeId) {
						resetForm();
					}
					dispatch(loadProjectTypes());
				}
			} finally {
				setDeletingId(null);
			}
		},
		[dispatch, editingRecord, resetForm]
	);

	const columns = useMemo(
		() => [
			{
				title: "Project Type Name",
				dataIndex: "ProjectTypeName",
				key: "ProjectTypeName",
			},
			{
				title: "Description",
				dataIndex: "ProjectTypeDescription",
				key: "ProjectTypeDescription",
				render: (value) => value || "-",
			},
			{
				title: "Status",
				dataIndex: "DataStatus",
				key: "DataStatus",
				render: (value) => {
					const numeric = Number(value);
					if (numeric === 1) return "Active";
					if (numeric === 0) return "Inactive";
					if (numeric === 2) return "Deleted";
					return value ?? "-";
				},
			},
			{
				title: "Actions",
				key: "actions",
				render: (_, record) => (
					<Space>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						/>
						<Popconfirm
							title="Delete project type"
							description={`Are you sure you want to delete ${record.ProjectTypeName}?`}
							okText="Yes"
							cancelText="No"
							onConfirm={() => handleDelete(record)}
						>
							<Button
								type="link"
								danger
								icon={<DeleteOutlined />}
								loading={deletingId === record.ProjectTypeId}
							/>
						</Popconfirm>
					</Space>
				),
			},
		],
		[deletingId, handleDelete, handleEdit]
	);

	const isLogged = Boolean(localStorage.getItem("isLogged"));
	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	return (
		<div className="add-project-type-page-wrapper">
			
			<Row gutter={[16, 16]} className="add-project-type-row">
				<Col xs={24} md={8} lg={7} className="column-design border rounded card-custom add-project-type-form-col">
					<div className="flex justify-between items-center mb-4">
							<Typography.Title level={4} style={{ margin: 0 }}>
								{editingRecord ? "Update Project Type" : "Add Project Type"}
							</Typography.Title>
							<Button type="link" icon={<PlusOutlined />} onClick={handleAddNew}>
								New
							</Button>
					</div>
						<Form
							form={form}
							layout="vertical"
							onFinish={handleSubmit}
							autoComplete="off"
						>
							<Form.Item
								label="Project Type Name"
								name="ProjectTypeName"
								rules={[
									{ required: true, message: "Please enter project type name" },
								]}
							>
								<Input placeholder="Enter project type name" />
							</Form.Item>

							<Form.Item
								label="Description"
								name="ProjectTypeDescription"
							>
								<Input.TextArea
									rows={3}
									placeholder="Enter description (optional)"
								/>
							</Form.Item>

							<Form.Item>
								<Space>
									<Button
										type="primary"
										htmlType="submit"
										loading={submitting}
									>
										{editingRecord ? "Update" : "Add"}
									</Button>
									{editingRecord ? (
										<Button onClick={handleCancelEdit} disabled={submitting}>
											Cancel
										</Button>
									) : null}
								</Space>
							</Form.Item>
						</Form>
				</Col>
				<Col xs={24} md={16} lg={17} className="add-project-type-table-col">
					<div className="ant-card p-4 rounded project-type-table-container">
						<div className="flex my-2 justify-between">
							<div className="w-50">
								<h4 className="text-xl mb-2">Project Types</h4>
							</div>
						</div>
						{loading ? (
							<div className="text-center py-8">
								<div className="ant-spin ant-spin-lg ant-spin-spinning">
									<span className="ant-spin-dot ant-spin-dot-spin">
										<i className="ant-spin-dot-item"></i>
										<i className="ant-spin-dot-item"></i>
										<i className="ant-spin-dot-item"></i>
										<i className="ant-spin-dot-item"></i>
									</span>
								</div>
								<p className="mt-4 text-gray-600">Loading project types...</p>
							</div>
						) : !list || list.length === 0 ? (
							<div className="text-center py-8">
								<p className="text-gray-600">No project types found.</p>
							</div>
						) : (
						<Table
							rowKey={(record) => record?.ProjectTypeId ?? record?.key}
							dataSource={list}
							columns={columns}
							loading={loading}
								scroll={{ x: true, y: "100%" }}
								pagination={{
									defaultPageSize: 20,
								}}
								className="project-type-table"
						/>
						)}
					</div>
				</Col>
			</Row>
		</div>
	);
};

export default ProjectTypeSetup;

